import React, { useEffect, useState } from "react";
import NotificationPopup from "./NotificationPopup";
import { fetchToken, onMessageListener, messaging } from "../../firebase";
import { setFcm } from "../../utils/connectStore";
import rejectedIcon from "../../Assets/Icons/rejectedIcon.svg";
import approvedIcon from "../../Assets/Icons/approvedIcon.svg";
import { useSelector, useDispatch } from "react-redux";
import { addNewNotification } from "../../redux/slices/notificationSlice";
import {
  MERCHANT_NOTIFICATION_TYPES,
  FUNDRAISER_NOTIFICATION_TYPES,
  formatNotificationMessage,
} from "../../utils/notificationTypes";
import GenericPopup from "./GenericPopup";
import PrimaryButton from "./PrimaryButton";
import PrimaryOutlineButton from "./PrimaryOutlineButton";

const WebNotifications = () => {
  const [notification, setNotification] = useState(null);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role,
  );

  console.log("notification", notification);

  const registerNotificationToken = async () => {
    if ("Notification" in window && messaging) {
      try {
        const token = await fetchToken();
        console.log("Token====>", token);
        setFcm({ fcmToken: token });
      } catch (e) {
        console.log("catch the error>>>", e);
      }
    }
  };

  const getNotificationIcon = (type, body) => {
    // For merchant-specific notifications
    if (role === "merchant") {
      switch (type) {
        case MERCHANT_NOTIFICATION_TYPES.OFFER_APPROVED:
        case MERCHANT_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
          return approvedIcon;
        case MERCHANT_NOTIFICATION_TYPES.OFFER_REJECTED:
          return rejectedIcon;
        case MERCHANT_NOTIFICATION_TYPES.NEW_REDEMPTION:
        case MERCHANT_NOTIFICATION_TYPES.LOW_STOCK:
        case MERCHANT_NOTIFICATION_TYPES.CAMPAIGN_END:
        case MERCHANT_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
        case MERCHANT_NOTIFICATION_TYPES.NEW_CUSTOMER:
          return null; // Will use default icon
        default:
          // Fallback to body content check
          if (body?.includes("approved")) return approvedIcon;
          if (body?.includes("rejected")) return rejectedIcon;
          return null;
      }
    } else if (role === "fundraiser") {
      switch (type) {
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_APPROVED:
        case FUNDRAISER_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
        case FUNDRAISER_NOTIFICATION_TYPES.FUNDS_DEPOSITED:
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_APPROVED:
          return approvedIcon;
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_REJECTED:
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_REJECTED:
          return rejectedIcon;
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_REFERRAL:
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_DONOR:
        case FUNDRAISER_NOTIFICATION_TYPES.CAMPAIGN_END:
        case FUNDRAISER_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return null; // Will use default icon
        default:
          // Fallback to body content check
          if (body?.includes("approved")) return approvedIcon;
          if (body?.includes("rejected")) return rejectedIcon;
          return null;
      }
    }

    // Default fallback
    if (body?.includes("approved")) return approvedIcon;
    if (body?.includes("rejected")) return rejectedIcon;
    return null;
  };

  const getNotificationTitle = (type, body) => {
    if (role === "merchant") {
      switch (type) {
        case MERCHANT_NOTIFICATION_TYPES.OFFER_APPROVED:
          return "Offer Approved";
        case MERCHANT_NOTIFICATION_TYPES.OFFER_REJECTED:
          return "Offer Rejected";
        case MERCHANT_NOTIFICATION_TYPES.NEW_REDEMPTION:
          return "New Redemption";
        case MERCHANT_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
          return "Payment Received";
        case MERCHANT_NOTIFICATION_TYPES.LOW_STOCK:
          return "Low Stock Alert";
        case MERCHANT_NOTIFICATION_TYPES.CAMPAIGN_END:
          return "Campaign Ending Soon";
        case MERCHANT_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return "System Maintenance";
        case MERCHANT_NOTIFICATION_TYPES.NEW_CUSTOMER:
          return "New Customer";
        default:
          return "New Notification";
      }
    } else if (role === "fundraiser") {
      switch (type) {
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_APPROVED:
          return "Referral Approved";
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_REJECTED:
          return "Referral Rejected";
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_REFERRAL:
          return "New Referral";
        case FUNDRAISER_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
          return "Payment Received";
        case FUNDRAISER_NOTIFICATION_TYPES.FUNDS_DEPOSITED:
          return "Funds Deposited";
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_APPROVED:
          return "Withdrawal Approved";
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_REJECTED:
          return "Withdrawal Rejected";
        case FUNDRAISER_NOTIFICATION_TYPES.CAMPAIGN_END:
          return "Campaign Ending Soon";
        case FUNDRAISER_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return "System Maintenance";
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_DONOR:
          return "New Donor";
        default:
          return "New Notification";
      }
    }
    return "New Notification";
  };

  useEffect(() => {
    console.log("webNotifications");
    if ("Notification" in window) {
      const currentPermission = Notification.permission;
      if (currentPermission === "granted") {
        registerNotificationToken();
      } else if (currentPermission === "denied") {
        const hasShown = sessionStorage.getItem(
          "has_shown_notification_denied_warning",
        );
        if (!hasShown) {
          setShowInstructions(true);
          setShowPermissionDeniedModal(true);
          sessionStorage.setItem(
            "has_shown_notification_denied_warning",
            "true",
          );
        }
      } else {
        const hasShown = sessionStorage.getItem(
          "has_shown_notification_denied_warning",
        );
        if (!hasShown) {
          setShowInstructions(false);
          setShowPermissionDeniedModal(true);
          sessionStorage.setItem(
            "has_shown_notification_denied_warning",
            "true",
          );
        }
      }
    }
  }, []);

  onMessageListener()
    .then((payload) => {
      console.log("notification payload", payload);

      const notificationType =
        payload?.data?.type || payload?.notification?.data?.type;
      const notificationBody =
        payload?.notification?.body || payload?.data?.message;
      const notificationTitle =
        payload?.notification?.title ||
        getNotificationTitle(notificationType, notificationBody);

      // Extract additional data for notifications
      const notificationData = payload?.data || {};

      const newNotification = {
        title: notificationTitle,
        body: notificationBody,
        icon: getNotificationIcon(notificationType, notificationBody),
        type: notificationType,
        timestamp: new Date().toISOString(),
        isRead: false,
        data: notificationData,
      };

      setNotification(newNotification);

      // Add to Redux store for real-time updates
      dispatch(
        addNewNotification({
          id: Date.now(), // Temporary ID
          type: notificationType,
          message: notificationBody,
          title: notificationTitle,
          createdAt: new Date().toISOString(),
          isRead: false,
          data: notificationData,
        }),
      );
    })
    .catch((err) => console.log("failed: ", err));

  return (
    <>
      {notification !== null && (
        <NotificationPopup
          heading={notification?.title}
          content={notification?.body}
          image={notification?.icon ? notification?.icon : null}
          setNotification={setNotification}
        />
      )}
      {showPermissionDeniedModal && (
        <GenericPopup
          content={
            showInstructions
              ? "Notification permission is blocked in your browser. Please click the lock icon in the address bar next to the URL and change Notifications to 'Allow' to receive important updates and alerts."
              : "Please allow notifications from the Swoodle Portal to receive important updates and alerts."
          }
          popupClassName="w-[335px] sm:w-[470px] pb-8 pt-10"
          cancelBtn={
            showInstructions ? null : (
              <PrimaryOutlineButton
                label="Deny"
                className="px-6 rounded-[8px] mt-6 transition-all hover:bg-[#F0FBFF]"
                onClick={() => setShowPermissionDeniedModal(false)}
              />
            )
          }
          continueBtn={
            showInstructions ? (
              <PrimaryButton
                label="Got It"
                className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all hover:opacity-90"
                onClick={() => {
                  setShowInstructions(false);
                  setShowPermissionDeniedModal(false);
                }}
              />
            ) : (
              <PrimaryButton
                label="Allow"
                className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all hover:opacity-90"
                onClick={async () => {
                  if (Notification.permission === "denied") {
                    setShowInstructions(true);
                  } else {
                    try {
                      const permission = await Notification?.requestPermission();
                      if (permission === "granted") {
                        registerNotificationToken();
                      } else if (permission === "denied") {
                        setShowInstructions(true);
                        return;
                      }
                    } catch (e) {
                      console.log("Permission request error:", e);
                    }
                    setShowPermissionDeniedModal(false);
                  }
                }}
              />
            )
          }
        />
      )}
    </>
  );
};

export default WebNotifications;
